<template>
  <div
    v-show="isVisible"
    class="scroll-minimap"
    :class="wrapperClass"
    role="navigation"
    aria-label="Scroll minimap"
  >
    <div
      ref="track"
      class="scroll-minimap__track"
      :style="trackStyle"
      @click="onTrackClick"
    >
      <div
        v-for="(segment, index) in segments"
        :key="`segment-${index}`"
        class="scroll-minimap__segment"
        :style="segmentStyle(segment)"
      />
      <div
        class="scroll-minimap__viewport"
        :style="viewportStyle"
        @mousedown.stop="onViewportDragStart"
      />
    </div>
  </div>
</template>

<script>
const REFRESH_DEBOUNCE_MS = 100;

export default {
  name: 'ScrollMinimap',
  props: {
    container: {
      default: null,
      validator(value) {
        return value === null || value instanceof HTMLElement;
      },
    },
    itemSelector: {
      type: String,
      default: '',
    },
    wrapperClass: {
      type: String,
      default: '',
    },
    trackWidth: {
      type: Number,
      default: 88,
    },
    trackHeight: {
      type: Number,
      default: 36,
    },
  },
  data() {
    return {
      segments: [],
      viewport: { left: 0, width: 0 },
      isVisible: false,
      isDragging: false,
    };
  },
  computed: {
    viewportStyle() {
      return {
        left: `${this.viewport.left}%`,
        width: `${this.viewport.width}%`,
      };
    },
    trackStyle() {
      return {
        width: `${this.trackWidth}px`,
        height: `${this.trackHeight}px`,
      };
    },
  },
  watch: {
    container: {
      immediate: true,
      handler(newContainer, oldContainer) {
        this.unbindContainer(oldContainer);
        this.bindContainer(newContainer);
        this.refresh();
      },
    },
    itemSelector() {
      this.refresh();
    },
  },
  mounted() {
    window.addEventListener('resize', this.onWindowResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
    this.unbindContainer(this.container);
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }
  },
  methods: {
    segmentStyle(segment) {
      return {
        left: `${segment.left}%`,
        width: `${segment.width}%`,
      };
    },
    bindContainer(element) {
      if (!element) return;

      element.addEventListener('scroll', this.onContainerScroll, {
        passive: true,
      });

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this.scheduleRefresh);
        this.resizeObserver.observe(element);
      }

      if (typeof MutationObserver !== 'undefined') {
        this.mutationObserver = new MutationObserver(this.scheduleRefresh);
        this.mutationObserver.observe(element, {
          childList: true,
          subtree: true,
          attributes: true,
        });
      }
    },
    unbindContainer(element) {
      if (!element) return;

      element.removeEventListener('scroll', this.onContainerScroll);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
        this.mutationObserver = null;
      }
    },
    onContainerScroll() {
      this.updateViewport();
    },
    onWindowResize() {
      this.scheduleRefresh();
    },
    scheduleRefresh() {
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
      }

      this.refreshTimer = setTimeout(() => {
        this.refresh();
      }, REFRESH_DEBOUNCE_MS);
    },
    refresh() {
      if (!this.container) {
        this.isVisible = false;
        this.segments = [];
        return;
      }

      this.updateSegments();
      this.updateViewport();
    },
    updateSegments() {
      if (!this.container || !this.itemSelector) {
        this.segments = [];
        return;
      }

      const items = Array.from(
        this.container.querySelectorAll(this.itemSelector)
      );

      if (!items.length) {
        this.segments = [];
        return;
      }

      const scrollWidth = this.container.scrollWidth;
      if (!scrollWidth) {
        this.segments = [];
        return;
      }

      const containerRect = this.container.getBoundingClientRect();

      this.segments = items.map(item => {
        const itemRect = item.getBoundingClientRect();
        const itemStart =
          itemRect.left - containerRect.left + this.container.scrollLeft;
        const itemWidth = itemRect.width;

        return {
          left: (itemStart / scrollWidth) * 100,
          width: (itemWidth / scrollWidth) * 100,
        };
      });
    },
    updateViewport() {
      if (!this.container) {
        this.isVisible = false;
        return;
      }

      const { scrollWidth, clientWidth, scrollLeft } = this.container;
      const hasOverflow = scrollWidth > clientWidth + 1;

      this.isVisible = hasOverflow && this.segments.length > 0;

      if (!hasOverflow) {
        this.viewport = { left: 0, width: 100 };
        return;
      }

      this.viewport = {
        left: (scrollLeft / scrollWidth) * 100,
        width: (clientWidth / scrollWidth) * 100,
      };
    },
    onTrackClick(event) {
      if (!this.container || this.isDragging) return;

      const track = this.$refs.track;
      if (!track) return;

      const rect = track.getBoundingClientRect();
      const clickRatio = (event.clientX - rect.left) / rect.width;
      const maxScrollLeft = this.container.scrollWidth - this.container.clientWidth;
      const targetScrollLeft = clickRatio * maxScrollLeft;

      this.container.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth',
      });
    },
    onViewportDragStart(event) {
      if (!this.container) return;

      event.preventDefault();
      this.isDragging = true;

      const track = this.$refs.track;
      const rect = track.getBoundingClientRect();
      const maxScrollLeft = this.container.scrollWidth - this.container.clientWidth;

      const onMouseMove = moveEvent => {
        const ratio = (moveEvent.clientX - rect.left) / rect.width;
        const clampedRatio = Math.min(Math.max(ratio, 0), 1);
        this.container.scrollLeft = clampedRatio * maxScrollLeft;
      };

      const onMouseUp = () => {
        this.isDragging = false;
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    },
  },
};
</script>

<style lang="scss" scoped>
.scroll-minimap {
  @apply fixed z-20 pointer-events-none;
  bottom: 3.25rem;
  right: 1.25rem;
}

.scroll-minimap__track {
  @apply relative bg-white dark:bg-slate-800 rounded-lg shadow-md pointer-events-auto cursor-pointer;
  width: 5.5rem;
  height: 2.25rem;
  padding: 0.25rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.scroll-minimap__segment {
  @apply absolute rounded-sm border border-solid border-slate-200 dark:border-slate-600 bg-slate-100 dark:bg-slate-700;
  top: 0.25rem;
  bottom: 0.25rem;
  min-width: 2px;
}

.scroll-minimap__viewport {
  @apply absolute rounded-sm pointer-events-auto cursor-grab;
  top: 0.125rem;
  bottom: 0.125rem;
  border: 2px solid #40518a;
  box-sizing: border-box;
  z-index: 1;
}
</style>
